import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Avatar from "./Avatar";
import { pickOverwhelmCards, pickOverwhelmOpener } from "@/lib/overwhelmTasks";

// Soft pastel tones for the 3 cards. Deliberately gentle — no priority
// colors, no urgency, no overdue badges. Card metadata (due dates, etc.)
// is intentionally omitted: this is a "less is kinder" surface.
const CARD_TONES = [
    "bg-sage-soft/55 ring-sage/30",       // easy win
    "bg-mustard-soft/55 ring-mustard/30",  // one step
    "bg-terracotta-soft/55 ring-terracotta/30", // just for you
];

// 75vh bottom sheet. Slide-up over 350ms with ease-out. Drag-down dismisses.
// Backdrop tap dismisses. The dim is applied to everything underneath, NOT
// inside the overlay — see App.js for the dim wrapper.
export default function OverwhelmOverlay({
    open,
    tasks,
    habits,
    todayKey,
    avatarStyle = "feminine",
    onPickAndStart,   // (cardSourceTask) => void   — primary "let's start"
    onJustAMinute,    // () => void                 — secondary + dismiss paths
}) {
    const cards = useMemo(
        () => pickOverwhelmCards({ tasks, habits, todayKey }),
        [tasks, habits, todayKey]
    );
    const [opener, setOpener] = useState("");
    const [selectedId, setSelectedId] = useState(null);
    const [mochiLine, setMochiLine] = useState("");

    // Reset per-open: fresh opener, no selection.
    useEffect(() => {
        if (open) {
            const line = pickOverwhelmOpener();
            setOpener(line);
            setMochiLine(line);
            setSelectedId(null);
        }
    }, [open]);

    const handleSelect = (card) => {
        setSelectedId(card.id);
        setMochiLine("okay. just that one. everything else can wait.");
    };

    const selected = cards.find((c) => c.id === selectedId);

    return (
        <AnimatePresence>
            {open && (
                <>
                    {/* Dimmed backdrop catcher — tap dismisses. Lives just above
                        the rest of the app (z-40) and below the sheet (z-50). */}
                    <motion.div
                        key="overwhelm-backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        className="fixed inset-0 z-40 bg-warm-900/30"
                        onClick={onJustAMinute}
                        data-testid="overwhelm-backdrop"
                        aria-hidden="true"
                    />
                    {/* The sheet itself */}
                    <motion.div
                        key="overwhelm-sheet"
                        initial={{ y: "100%" }}
                        animate={{ y: 0 }}
                        exit={{ y: "100%" }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        drag="y"
                        dragConstraints={{ top: 0, bottom: 0 }}
                        dragElastic={{ top: 0, bottom: 0.6 }}
                        onDragEnd={(_, info) => {
                            // Swipe-down dismiss threshold
                            if (info.offset.y > 110 || info.velocity.y > 600) {
                                onJustAMinute?.();
                            }
                        }}
                        className="fixed left-0 right-0 bottom-0 z-50 bg-cream rounded-t-3xl shadow-float border-t border-warm-300/40"
                        style={{ height: "75vh", paddingBottom: "max(env(safe-area-inset-bottom), 16px)" }}
                        data-testid="overwhelm-overlay"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Overwhelm support flow"
                    >
                        {/* drag handle */}
                        <div className="flex justify-center pt-3 pb-1">
                            <div className="w-10 h-1.5 rounded-full bg-warm-300/50" />
                        </div>

                        <div className="h-full flex flex-col px-5 pb-2 overflow-hidden">
                            {/* Top — Mochi + message */}
                            <div className="flex flex-col items-center text-center pt-1">
                                <Avatar mood="determined" size={86} style={avatarStyle} />
                                <div
                                    className="mt-3 bg-warm-100/70 rounded-2xl px-4 py-2.5 max-w-[300px] font-body text-warm-900 text-sm leading-snug"
                                    data-testid="overwhelm-message"
                                >
                                    {mochiLine || opener}
                                </div>
                            </div>

                            {/* Middle — three (or fewer) cards */}
                            <div className="flex-1 min-h-0 overflow-y-auto mt-4">
                                {cards.length === 0 ? (
                                    <div className="text-center text-warm-700 font-body text-sm py-12 px-4">
                                        nothing on your plate right now. just take a beat. 🌸
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-3">
                                        {cards.map((card, idx) => {
                                            const isSelected = selectedId === card.id;
                                            return (
                                                <motion.button
                                                    key={card.id}
                                                    type="button"
                                                    onClick={() => handleSelect(card)}
                                                    whileTap={{ scale: 0.98 }}
                                                    className={`relative w-full text-left rounded-2xl px-4 py-3.5 ring-2 transition-colors ${CARD_TONES[idx % CARD_TONES.length]} ${
                                                        isSelected ? "ring-warm-900/40" : "hover:ring-warm-300/60"
                                                    }`}
                                                    data-testid={`overwhelm-card-${idx}`}
                                                >
                                                    <span className="absolute top-2 right-3 text-[10px] uppercase tracking-widest font-display text-warm-900/70">
                                                        {card.label} {card.emoji}
                                                    </span>
                                                    <p className="pr-24 font-body font-bold text-warm-900 leading-snug">
                                                        {card.title}
                                                    </p>
                                                </motion.button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Bottom — primary + secondary */}
                            <div className="pt-3 pb-1 flex flex-col items-center gap-2.5">
                                <button
                                    type="button"
                                    onClick={() => selected && onPickAndStart?.(selected)}
                                    disabled={!selected}
                                    className={`w-full max-w-sm font-display text-base px-5 py-3 rounded-full transition-all active:scale-95 ${
                                        selected
                                            ? "bg-terracotta hover:bg-terracotta-hover text-cream shadow-clay"
                                            : "bg-warm-300/40 text-warm-700 cursor-not-allowed"
                                    }`}
                                    data-testid="overwhelm-start"
                                >
                                    let's start
                                </button>
                                <button
                                    type="button"
                                    onClick={onJustAMinute}
                                    className="font-body text-sm text-warm-700 hover:text-warm-900 underline-offset-2 hover:underline transition-colors"
                                    data-testid="overwhelm-minute"
                                >
                                    i just need a minute
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
